import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { downloadBlob } from "@/components/ToolLayout";
import { useExportGate } from "@/hooks/useExportGate";
import { ExportUsageNote } from "@/components/ExportUsageNote";
import { applyBackup, buildBackup, clearSaved, parseBackup } from "@/lib/backup";

/** Save / restore / clear a tool's browser autosave. Everything stays on this device. */
export function BackupBar({ tool, prefixes, session = false, hideClear = false, transformRestore }: { tool: string; prefixes: string[]; /** true when the tool saves for this visit only (sessionStorage) */ session?: boolean; /** the tool offers its own Clear (in-page confirm with Undo) */ hideClear?: boolean; /** lets a tool rebuild its saved keys from a richer backup before they are written */ transformRestore?: (entries: Record<string, unknown>) => Record<string, unknown> }) {
  const [error, setError] = useState("");
  const store = () => (session ? sessionStorage : localStorage);
  const input = useRef<HTMLInputElement>(null);
  const { checkExport } = useExportGate();

  const save = async () => {
    if (!(await checkExport())) return; // paywall is open, no file
    try {
      const b = buildBackup(tool, prefixes, store());
      downloadBlob(new Blob([JSON.stringify(b, null, 2)], { type: "application/json" }), `${tool}-backup.json`);
    } catch { toast.error("Couldn't read the saved data in this browser"); }
  };
  const restore = async (f: File | undefined) => {
    if (!f) return;
    setError("");
    const r = parseBackup(await f.text(), tool, prefixes);
    if (!r.ok) { setError(r.error); toast.error(r.error); return; }
    try { applyBackup(transformRestore ? transformRestore(r.entries) : r.entries, prefixes, store()); } catch { toast.error("Couldn't write to this browser's storage"); return; }
    toast.success("Backup restored");
    setTimeout(() => window.location.reload(), 400);
  };
  const clear = () => {
    if (!window.confirm("Clear everything this tool has saved?")) return;
    try { clearSaved(prefixes, store()); } catch { /* ignore */ }
    window.location.reload();
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mt-4 text-xs text-muted-foreground">
      <span>{session ? "Kept while this tab is open, then erased. Back up to continue later." : "Saved automatically in this browser only."}</span>
      <Button variant="outline" size="sm" onClick={() => void save()}><Download className="h-3.5 w-3.5 mr-1" />Back up</Button>
      <Button variant="outline" size="sm" onClick={() => input.current?.click()}><Upload className="h-3.5 w-3.5 mr-1" />Restore</Button>
      {!hideClear && <Button variant="ghost" size="sm" onClick={clear}><Trash2 className="h-3.5 w-3.5 mr-1" />Clear</Button>}
      <ExportUsageNote premiumOrZeroOnly className="basis-full" />
      {error && <p role="alert" className="basis-full text-xs text-destructive">{error}</p>}
      <input ref={input} type="file" accept=".json,application/json" className="hidden" onChange={(e) => { void restore(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}
